/**
 *
 * AbsenceListItem
 *
 */

import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

// antd component
import { List, Tag } from 'antd';

// icons
import { CheckOutlined, CloseOutlined, ExclamationCircleOutlined, QuestionOutlined } from '@ant-design/icons';
import CustomIcons from 'components/CustomIcons';

import {
  getAbsenceReason
} from 'utils/helpers/helper.generateData';
import { ABS_TYPES_LIST_OPTIONS } from 'utils/helpers/helper.constant';
import _local from 'utils/localStorageUtils';
import { convertTimeToString, convertDateToWeekDay, convertDateToString } from "utils/moment/moment.utils";

function AbsenceListItem({
  element,
  searchString,
  // formats
  timeFormat,
  dateFormat,
  // func
  customHighlighter,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames('absence-list-meta', className);
  const { t, i18n } = useTranslation();

  const localUser = _local.getLocalUser();
  const reason = element?.type == "no-medical" && getAbsenceReason(element);
  
  const studentName = element?.student?.description
    ? `${element?.student?.description?.lastname} ${element?.student?.description?.firstname}`
    : '';
  
  const getAbsenceStatusIcon = type => {
    switch (type) {
      case 'pending':
        return <CustomIcons type="loop" />;
      case 'justification-missing':
        return <CustomIcons type="document" />;
      case 'sick':
        return <CustomIcons type="loop" />;
      case 'sickness-finish':
        return <CheckOutlined />;
      case 'accepted':
        return <CheckOutlined />;
      case 'refused':
        return <CloseOutlined />;
      case 'unjustified':
        return <QuestionOutlined />;
    }
  };

  const getAbsencesTypes = useCallback(() => ABS_TYPES_LIST_OPTIONS(), [i18n?.language]);
  
  return element && (
    <List.Item.Meta
      className={childClassNames}
      title={false}
      description={
        <>
          <div className="absence-description">

            <div className="abcence-wrapper-for-reason-processed">
              <span className={`status-tag status-tag--${element?.status}`}>
                {getAbsenceStatusIcon(element?.status)}
              </span>
            </div>

            <div className="absence-wrapper-date">

              <div className="absence-date__row">
                <div className="column helper">{t("common.from")}</div>
                <div className="column day">{!!element?.date_from && convertDateToWeekDay(element?.date_from, "dd")}{' '}</div>
                <div className="column">
                  {!!element?.date_from && convertDateToString(element?.date_from)}
                </div>
                <div className="column time">
                  {!!element?.time_from && convertTimeToString(element?.time_from)}
                  {(!!!element?.date_to && !!!element?.recurring_date && !!element?.time_to) && (
                    ' - '+ convertTimeToString(element?.time_to)
                  )}
                </div>
              </div>
              {(!!element?.date_to || !!element?.recurring_date) && (
                <div className="absence-date__row">
                  <div className="column helper">{t("common.to")}</div>
                  <div className="column day">
                    {!!element?.recurring_date
                      ? convertDateToWeekDay(element?.recurring_date, "dd")
                      : (
                        !!element?.date_to
                        ? convertDateToWeekDay(element?.date_to, "dd")
                        : convertDateToWeekDay(element?.date_from, "dd")
                      )}{' '}
                  </div>
                  <div className="column">
                    {!!element?.recurring_date
                      ? convertDateToString(element?.recurring_date)
                      : (
                        !!element?.date_to
                        ? convertDateToString(element?.date_to)
                        : convertDateToString(element?.date_from)
                      )}
                  </div>
                  <div className="column time">
                    {!!element?.time_to ? convertTimeToString(element?.time_to) : convertTimeToString(element?.time_from)}
                  </div>
                </div>
              )}
              
              {/* {absenceDuration && (
                <div className="absence-date__row">
                  <div className="column helper big">{t("absences.duration")}:</div>
                  <div className="column">
                    <span className="absence-time">{absenceDuration}</span>
                  </div>
                </div>
              )} */}
            </div>

            <div className="absence-title">
              {element?.type === 'sickness' ? (
                <span className="title">
                  {getAbsencesTypes()?.find(e=>e?.key==element?.type)?.title}
                </span>
              ) : (
                <span className="title">
                  {getAbsencesTypes()?.find(e=>e?.key==element?.type)?.title}{!!reason ? ` : ${reason}` : ''}
                </span>
              )}
            </div>

            {(!!element?.origin || !!element?.original || !!element?.unjustified) && (
              <span className="tags-abs">
                {!!element?.original && <Tag className="abs-tag original">{t("absences.recurring")}</Tag>}
                {!!element?.origin && <Tag className="abs-tag origin">{t("absences.reccurence")}</Tag>}
                {!!element?.unjustified && <Tag className="abs-tag unjustified">{t("global.absenceTypes.unjustified")}</Tag>}
              </span>
            )}
          
            {localUser?.role == 'teacher' && studentName && (
              <div className='absence-student'>
                <CustomIcons type="academic" className="icon-student" />
                {studentName}
              </div>
            )}
            {localUser?.role == 'parent' && element?.status == 'justification-missing' && (
              <span className="need-acition-message">
                <ExclamationCircleOutlined /> {t("inputs.actionRequired")}
              </span>
            )}
          </div>
        </>
      }
    />
  );

};

AbsenceListItem.defaultProps = {
  dateFormat: 'DD.MM.YYYY',
  timeFormat: 'HH:mm',
};
AbsenceListItem.propTypes = {
  element: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.bool.isRequired,
  ]),
  searchString: PropTypes.string,
  customHighlighter: PropTypes.func,
};

export default memo(AbsenceListItem);